import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAreasQuery, useIterationsQuery } from '@platform/state/api';
import { Picker } from '@platform/ui';
import { formatDate, todayIso } from '@platform/core/lib/format';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import type { IControlProps } from '../IControlProps';
import { treeOptions } from '../options/treeOptions';

/**
 * Шлях напряму або ітерації (UIUX/field-types/area-path, iteration-path; controls/tree-picker): пікер дерева з відступом 16px на рівень,
 * пошуком завжди, крапкою кольору напряму (успадковується від предка), датами спринтів, бейджем «Поточний» і приглушеними минулими.
 */
export function TreePicker({ id, value, onChange, readOnly, field, rt, ws, size, prefix, invalid, placeholder, autoFocus }: IControlProps<string>) {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const strings = useModuleStrings();
  const isArea = field.type === 'area';
  const areas = useAreasQuery(ws, { skip: !isArea });
  const iterations = useIterationsQuery(ws, { skip: isArea });
  const nodes = (isArea ? areas.data : iterations.data);
  const options = useMemo(() => treeOptions(nodes ?? [], isArea ? 'area' : 'iteration', todayIso(), {
    current: t('fieldControls.current'),
    range: (a, b) => `${formatDate(a, lang)} – ${formatDate(b, lang)}`,
  }), [nodes, isArea, t, lang]);
  return (
    <Picker id={id} value={value ?? ''} options={[{ value: '', label: t('fieldControls.notSet'), muted: true }, ...options]}
      onChange={(v) => onChange(v || null)} label={strings.field(rt.key, field.key, field.label)} prefix={prefix} size={size}
      searchable readOnly={readOnly} invalid={invalid} placeholder={placeholder} autoFocus={autoFocus}
      loading={(isArea ? areas : iterations).isFetching} selected={value && !nodes ? { value, label: '…' } : null} />
  );
}
